import React, { useState, Fragment } from "react";
import {
  Modal,
  ModalHeader,
  Form,
  ModalBody,
  Input,
  Label,
  Row,
  Col,
  FormFeedback,
  FormGroup,
} from "reactstrap";

// ** Third Party Components
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast, ToastContainer } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createUser } from "api/mutators/user";
import { generateRandomString } from "common/utilis";
import Select from "react-select";
import { fetchRoles } from "api/queries/role";
import { useTranslation } from "react-i18next";

/**
 * Form Validation Schemayarn s
 */
const schema = yup.object().shape({
  name: yup.string().required("E' obbligatorio indicare il nome dell'utente"),
  surname: yup
    .string()
    .required("E' obbligatorio indicare il cognome dell'utente"),
  email: yup.string().email().required(),
  mainRole: yup.object().shape({
    value: yup.string(),
    label: yup.string(),
  }),
});

const defaultValues = {
  name: "",
  surname: "",
  email: "",
  mainRole: { label: "Select", value: "" }
};

interface UserModalProps {
  modal?: boolean;
  toggle?: React.MouseEventHandler<any>;
  setModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const UserModal: React.FC<UserModalProps> = ({ modal, toggle, setModal }) => {

  const { t } = useTranslation("translation", { keyPrefix: "USERS" });

  // ** Hook
  const {
    reset,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: createUser,
    onError: (error: any) => {
      toast.error("Error! User not created", { autoClose: 3000 });
    },
    onSuccess: () => {
      setModal(false);
      reset(defaultValues);
      queryClient.invalidateQueries({ queryKey: ["fetchUsers"] });
      toast.success("User Created Successfully", { autoClose: 3000 });
    },
  });

  function onSubmit() {
    mutation.mutate({
      ...getValues(),
      path: `${generateRandomString(10)}/`,
    });
  }

  const { isPending: isPendingRoles, data: optionRoles } = useQuery({
    queryKey: fetchRoles.queryKey(),
    queryFn: () => fetchRoles.queryFn(),
  });

  return (
    <Fragment>
      <Modal id="showModal" size={"xl"} isOpen={modal} toggle={toggle} centered>
        <ModalHeader className="bg-light p-3" toggle={toggle}>
        {t("MODALS:ADD")}
        </ModalHeader>
        <Form className="tablelist-form" onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <>
              <Row>
                <Col sx={6}>
                  <FormGroup>
                    <div className="mb-3">
                      <Label className="form-label" for="email">
                        {t("MODALS:NAME")}
                      </Label>
                      <Controller
                        control={control}
                        name="email"
                        render={({ field }) => (
                          <Input
                            {...field}
                            id="email"
                            placeholder="Insert Email User"
                            invalid={!!errors.email}
                          />
                        )}
                      />
                      {errors.email && (
                        <FormFeedback>{errors.email.message}</FormFeedback>
                      )}
                    </div>
                  </FormGroup>
                </Col>
                <Col sx={6}>
                  <FormGroup>
                    <Label className="form-label" for="landing">
                    {t("MODALS:ROLE")}
                    </Label>
                    <Controller
                      control={control}
                      name="mainRole"
                      render={({ field }) => (
                        <Select {...field} options={optionRoles} />
                      )}
                    />
                    {errors.mainRole && (
                      <FormFeedback>{errors.mainRole.message}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Label className="form-label" for="name">
                  {t("MODALS:NAME")}
                  </Label>
                  <Controller
                    control={control}
                    name="name"
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="name"
                        placeholder="Insert Name User"
                        invalid={!!errors.name}
                      />
                    )}
                  />
                  {errors.name && (
                    <FormFeedback>{errors.name.message}</FormFeedback>
                  )}
                </Col>
                <Col>
                  <Label className="form-label" for="name">
                  {t("MODALS:SURNAME")}
                  </Label>
                  <Controller
                    control={control}
                    name="surname"
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="surname"
                        placeholder="Insert Surname User"
                        invalid={!!errors.name}
                      />
                    )}
                  />
                  {errors.surname && (
                    <FormFeedback>{errors.surname.message}</FormFeedback>
                  )}
                </Col>
              </Row>
            </>
          </ModalBody>
          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => {
                  reset(defaultValues);
                  setModal(false);
                }}
              >
                {t("MODALS:CLOSE")}
              </button>
              <button type="submit" className="btn btn-soft-danger">
              {t("MODALS:SAVE")}
              </button>
            </div>
          </div>
        </Form>
      </Modal>
      <ToastContainer closeButton={false} limit={1} />
    </Fragment>
  );
};

export default UserModal;
